import type { BackOfficeGoogleAuthResponseDto } from 'api/swagger/definitions/backoffice'
import { isAxiosError } from 'axios'
import React from 'react'

export interface Session {
  role: SessionRole
  isLoading: boolean
  isError: boolean
  errorMessage: string | undefined
  timedOut: boolean
}

export enum SessionRole {
  ADMIN = 'ADMIN',
  SALES = 'SALES',
  UNAUTHORIZED = 'UNAUTHORIZED',
}

const defaultSetState: (newMeta: Partial<SessionRole>) => void = () => {
  throw new Error('SessionContextProvider not found')
}

interface SessionContextType {
  role: SessionRole
  getRemoteRole?: GetRemoteRole
  remoteSignout?: RemoteSignout
  errorMessage?: string
  isLoading: boolean
  timedOut: boolean
}

export const SessionContext = React.createContext<SessionContextType>({
  role: SessionRole.UNAUTHORIZED,
  getRemoteRole: undefined,
  remoteSignout: undefined,
  isLoading: true,
  timedOut: false,
})

interface GetRemoteRoleProps {
  enabled?: boolean
  timeout: number
}

interface GetRemoteRoleData {
  data: BackOfficeGoogleAuthResponseDto | undefined
  error: unknown | undefined
  isLoading: boolean
}

export type GetRemoteRole = (props: GetRemoteRoleProps) => GetRemoteRoleData

export type RemoteSignout = () => () => Promise<unknown> | void

interface SessionContextProviderProps {
  children: React.ReactNode
  value?: SessionRole
  getRemoteRole: GetRemoteRole
  remoteSignout: RemoteSignout
}

export const SessionContextProvider = ({
  children,
  value = SessionRole.UNAUTHORIZED,
  getRemoteRole,
  remoteSignout,
}: SessionContextProviderProps) => {
  let role = value

  const { data: type, error, isLoading } = getRemoteRole({ enabled: !isSignedIn(role), timeout: 3000 })

  let errorMessage: string | undefined
  let timedOut = false

  if (error) {
    if (isAxiosError(error)) {
      errorMessage = error.response?.data.message || error.response?.data.error
      timedOut = error.code === 'ECONNABORTED'
    } else {
      errorMessage = 'Unknown error occurred'
    }
  }

  if (type) {
    role = mapSessionRole(type)
  }

  return (
    <SessionContext.Provider value={{ role, remoteSignout, timedOut, getRemoteRole, errorMessage, isLoading }}>
      {children}
    </SessionContext.Provider>
  )
}

export const useSessionContext = () => React.useContext(SessionContext)

export const mapSessionRole = (type: BackOfficeGoogleAuthResponseDto) => {
  switch (type) {
    case 'BACKOFFICE':
    case 'BACKOFFICE_FULL':
    case 'BACKOFFICE_FULL_READ_ONLY':
      return SessionRole.ADMIN
    case 'BACKOFFICE_SALES':
      return SessionRole.SALES
    case 'UNAUTHORIZED':
      return SessionRole.UNAUTHORIZED
    default:
      return SessionRole.UNAUTHORIZED
  }
}

export const isSignedIn = (role: SessionRole) => [SessionRole.ADMIN, SessionRole.SALES].includes(role)

export const useIsSignedIn = () => {
  const { role } = useSessionContext()

  return isSignedIn(role)
}
